import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import deviceService from '../services/deviceService';
import { AUTH_TOKEN_KEY } from '../shared/consts';

// Create the context
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [primaryColor, setPrimaryColor] = useState(null);
  const [secondaryColor, setSecondaryColor] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null); // Cached logo as data URL
  const [loading, setLoading] = useState(true);

  // Load and cache logo image
  const loadLogoImage = useCallback(async (url) => {
    if (!url) return;

    try {
      // Fetch the logo image
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch logo');
      }

      // Convert the logo to a blob and then to a data URL
      const blob = await response.blob();
      const reader = new FileReader();

      reader.onloadend = () => {
        const dataUrl = reader.result;
        setLogoDataUrl(dataUrl);
      };

      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('Error loading logo:', err);
    }
  }, []);

  // Fetch device information from API
  const fetchDeviceInfo = async () => {
    try {
      const deviceData = await deviceService.getMe();
      setDeviceInfo(deviceData);
      setPrimaryColor(deviceData.primary_color || '#000000');
      setSecondaryColor(deviceData.secondary_color || '#FFFFFF');

      // Set logo URL and trigger logo loading
      const logoUrl = deviceData.location_logo || deviceData.company_logo;
      setLogoUrl(logoUrl);
      if (logoUrl) {
        loadLogoImage(logoUrl);
      }

      setIsAuthenticated(true);
    } catch (err) {
      console.error('Error fetching device info:', err);
      await logout();
    } finally {
      setLoading(false);
    }
  };

  // Initialize authentication state from cookies
  useEffect(() => {
    if (Cookies.get(AUTH_TOKEN_KEY)) {
      fetchDeviceInfo();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (api_key) => {
    const response = await deviceService.login(api_key);

    // Save auth token in cookie
    if (response && response.api_key) {
      // Set cookie with secure options and maximum expiration (100 years)
      Cookies.set(AUTH_TOKEN_KEY, response.api_key, {
        expires: 36500, // 100 years in days
        sameSite: 'strict',
        secure: window.location.protocol === 'https:' // only set secure in production
      });
      await fetchDeviceInfo();
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Try to logout on server, but continue with local logout even if it fails
      await deviceService.logout();
    } catch (err) {
      console.error('Error during logout:', err);
    } finally {
      Cookies.remove(AUTH_TOKEN_KEY);

      setDeviceInfo(null);
      setLogoUrl(null);
      setLogoDataUrl(null);
      setPrimaryColor(null);
      setSecondaryColor(null);
      setIsAuthenticated(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        deviceInfo,
        loading,
        login,
        logout,
        primaryColor,
        secondaryColor,
        logoUrl,
        logoDataUrl,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;